import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { useAuth } from '../api/auth'
import { useCreateProject, useDeleteProject, useProjects, useUpdateProject } from '../api/client'
import { inputClass, primaryButton } from '../components/AddParcelDialog'
import { de } from '../i18n/de'

export function Projects() {
  const [includeInactive, setIncludeInactive] = useState(false)
  const { data: projects, isLoading } = useProjects(includeInactive)
  const createProject = useCreateProject()
  const updateProject = useUpdateProject()
  const deleteProject = useDeleteProject()
  // Löschen ist Administratoren vorbehalten; der Server erzwingt das, hier wird nur der Knopf
  // ausgeblendet, damit niemand auf eine Aktion klickt, die ohnehin abgelehnt würde.
  const canDelete = useAuth().data?.user?.is_admin ?? false

  const [name, setName] = useState('')
  const [customer, setCustomer] = useState('')
  const [note, setNote] = useState<string | null>(null)

  function submit(event: React.FormEvent) {
    event.preventDefault()
    createProject.mutate(
      { name: name.trim(), customer: customer.trim() || undefined },
      {
        onSuccess: () => {
          setName('')
          setCustomer('')
        },
      },
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 px-4 py-6">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">{de.projects.title}</h1>
        <label className="flex items-center gap-2 text-sm text-slate-500">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          {de.projects.showInactive}
        </label>
      </div>

      <form
        onSubmit={submit}
        className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900"
      >
        <input
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={de.projects.name}
          className={inputClass + ' min-w-[10rem] flex-1'}
        />
        <input
          value={customer}
          onChange={(event) => setCustomer(event.target.value)}
          placeholder={de.projects.customer}
          className={inputClass + ' min-w-[10rem] flex-1'}
        />
        <button
          type="submit"
          disabled={!name.trim() || createProject.isPending}
          className={primaryButton + ' inline-flex items-center gap-1.5'}
        >
          <Plus className="h-4 w-4" aria-hidden />
          {de.projects.create}
        </button>
      </form>

      {createProject.isError && (
        <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
          {(createProject.error as Error).message}
        </p>
      )}
      {note && <p className="text-sm text-amber-700 dark:text-amber-400">{note}</p>}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-slate-400">…</p>
        ) : projects?.length ? (
          <ul>
            {projects.map((project) => (
              <li
                key={project.id}
                className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 last:border-0 dark:border-slate-800"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{project.name}</span>
                    {!project.is_active && (
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                        {de.projects.inactive}
                      </span>
                    )}
                  </div>
                  {project.customer && (
                    <p className="truncate text-xs text-slate-500">{project.customer}</p>
                  )}
                </div>

                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {project.parcel_count} {de.projects.parcelCount}
                </span>

                <label className="flex shrink-0 items-center gap-1.5 text-xs text-slate-500">
                  <input
                    type="checkbox"
                    checked={project.is_active}
                    onChange={(event) =>
                      updateProject.mutate({
                        id: project.id,
                        patch: { is_active: event.target.checked },
                      })
                    }
                  />
                  {de.projects.active}
                </label>

                {canDelete && (
                  <button
                    onClick={() => {
                      if (!window.confirm(de.projects.confirmDelete)) return
                      deleteProject.mutate(project.id, {
                        onSuccess: (result) =>
                          setNote(result.is_active ? null : de.projects.deactivated),
                      })
                    }}
                    className="shrink-0 rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950"
                    aria-label={de.actions.delete}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-4 py-8 text-center text-sm text-slate-400">{de.projects.empty}</p>
        )}
      </div>
    </div>
  )
}
